/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Utilities for the Quick Schedule review step.
 *
 * @file quickScheduleReviewUtils.js
 * @author Anuja Gaikwad
 * @since 2.0.3
 */

import Utils from '../../common/utils';
import { parseDate, formatDateToStorage } from '../../common/dateTimeUtils';
import { PREFIX_ASSIGNMENT, SCHEDULE_MENU_HIDDEN_ITEM_IDS } from '../../common/constants';
import {
  findNodeById,
  collectAssignedContentNodeIds,
  calculateQuestionsForNode,
  getUnassignedNodeForSchedule
} from '../chapter-content/chapterContentTreeUtils';

const DAY_KEY_TO_INDEX = {
  Su: 0,
  M: 1,
  T: 2,
  W: 3,
  Th: 4,
  F: 5,
  S: 6
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function toLocalCalendarDate(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function formatDateIso(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

export const ASSISTED_SCHEDULE_TREE_MENU_CONFIG = {
  items: [
    { id: SCHEDULE_MENU_HIDDEN_ITEM_IDS.ADD_TO_DATE },
    { id: SCHEDULE_MENU_HIDDEN_ITEM_IDS.PREVIEW, divider: true },
    { id: SCHEDULE_MENU_HIDDEN_ITEM_IDS.QUESTIONS }
  ],
  labels: {
    [SCHEDULE_MENU_HIDDEN_ITEM_IDS.ADD_TO_DATE]: 'Add to date',
    [SCHEDULE_MENU_HIDDEN_ITEM_IDS.PREVIEW]: 'Preview as student',
    [SCHEDULE_MENU_HIDDEN_ITEM_IDS.QUESTIONS]: 'Questions and topics'
  },
  hiddenAlways: [SCHEDULE_MENU_HIDDEN_ITEM_IDS.ADD_TO_DATE],
  hiddenWhenUnchecked: []
};

function parseScheduleCalendarDate(value) {
  if (!value) {
    return null;
  }

  const dateOnly = String(value).includes('T') ? String(value).split('T')[0] : value;

  return parseDate(dateOnly);
}

export const QUICK_SCHEDULE_ASSIGNMENT_LIST_ITEM_CONFIG = {
  showDragZone: true,
  showSections: true,
  showSectionRemoveButtons: true,
  inlineEditOnSelect: true,
  inlineTitleEdit: true,
  clickToEdit: true,
  showClearDataButton: true,
  showMenu: false,
  scheduleBuilderLayout: true
};

/**
 * Calendar min/max for the Add to Date picker from step 1 schedule bounds.
 */
export function getScheduleDateBounds(scheduleFormValues = {}) {
  const start = parseScheduleCalendarDate(scheduleFormValues.startDate);
  const end = parseScheduleCalendarDate(scheduleFormValues.endDate);

  if (!start || !end) {
    return { minDate: null, maxDate: null };
  }

  return {
    minDate: toLocalCalendarDate(start),
    maxDate: toLocalCalendarDate(end)
  };
}

/**
 * Whether a YYYY-MM-DD value sits inside the step 1 start/end date range.
 * Add to Date allows any calendar day in that window, not only configured due weekdays.
 */
export function isDateWithinScheduleRange(dateIso, scheduleFormValues = {}) {
  const date = parseScheduleCalendarDate(dateIso);
  const start = parseScheduleCalendarDate(scheduleFormValues.startDate);
  const end = parseScheduleCalendarDate(scheduleFormValues.endDate);

  if (!date || !start || !end) {
    return false;
  }

  const dateMs = toLocalCalendarDate(date).getTime();
  const startMs = toLocalCalendarDate(start).getTime();
  const endMs = toLocalCalendarDate(end).getTime();

  return dateMs >= startMs && dateMs <= endMs;
}

function assignmentHasContent(assignment) {
  return (assignment?.contentNodes || []).length > 0;
}

/** Dates that already have content on an assignment card. */
export function getOccupiedScheduleDates(assignments = [], { excludeAssignmentId } = {}) {
  return new Set(
    assignments
      .filter((assignment) => (
        assignment.id !== excludeAssignmentId && assignmentHasContent(assignment)
      ))
      .map((assignment) => assignment.date)
      .filter(Boolean)
  );
}

/** react-datepicker filterDate callback scoped to the step 1 date range. */
export function createScheduleAddToDateFilter(
  scheduleFormValues = {},
  assignments = [],
  { excludeAssignmentId } = {}
) {
  const occupiedDates = getOccupiedScheduleDates(assignments, { excludeAssignmentId });

  return (date) => {
    const dateIso = formatDateToStorage(date);

    if (!isDateWithinScheduleRange(dateIso, scheduleFormValues)) {
      return false;
    }

    return !occupiedDates.has(dateIso);
  };
}

/** RHF validate rule for the Add to Date modal. */
export function createScheduleAddToDateValidator(
  scheduleFormValues = {},
  assignments = [],
  { excludeAssignmentId, errorMessages = {} } = {}
) {
  const { required, outOfRange, occupied } = errorMessages;
  const occupiedDates = getOccupiedScheduleDates(assignments, { excludeAssignmentId });

  return (value) => {
    if (!value) {
      return required;
    }

    if (!isDateWithinScheduleRange(value, scheduleFormValues)) {
      return outOfRange;
    }

    if (occupiedDates.has(value)) {
      return occupied;
    }

    return true;
  };
}

function getNextAssignmentIndex(assignments = []) {
  return assignments.reduce((maxIndex, assignment) => {
    const match = String(assignment.id).match(/-qs-(\d+)/);

    if (!match) {
      return maxIndex;
    }

    return Math.max(maxIndex, Number.parseInt(match[1], 10));
  }, 0) + 1;
}

/** Locate the assignment card for a calendar date, if one already exists. */
export function findAssignmentByDate(assignments = [], dateIso) {
  if (!dateIso) {
    return null;
  }

  return assignments.find((assignment) => assignment.date === dateIso) || null;
}

/**
 * Build a new empty slot for a date outside the auto-generated weekday list.
 * Reuses step 1 due time and naming pattern from buildQuickScheduleAssignments.
 */
export function createAssignmentForDate({
  dateIso,
  scheduleFormValues = {},
  assignments = [],
  title
} = {}) {
  const nextIndex = getNextAssignmentIndex(assignments);
  const { dueTime = '' } = scheduleFormValues;

  return {
    id: `${PREFIX_ASSIGNMENT}-qs-${nextIndex}-${dateIso}`,
    title: title || `Assignment ${nextIndex}`,
    date: dateIso,
    time: dueTime,
    sections: [],
    chapters: [],
    contentNodes: []
  };
}

function upsertAssignmentByDate(assignments = [], assignment) {
  const existingIndex = assignments.findIndex((item) => item.date === assignment.date);

  if (existingIndex >= 0) {
    const nextAssignments = [...assignments];
    nextAssignments[existingIndex] = assignment;

    return nextAssignments;
  }

  return [...assignments, assignment].sort((left, right) => (
    String(left.date).localeCompare(String(right.date))
  ));
}

const getChapterNumbersFromTitle = (title) => (
  Utils.getAssignmentChaptersNumbers([title]) || []
);

/**
 * Derive the label shown on assignment section chips from a content tree node title.
 * Numbered sections use the section number; unnumbered sections use the section title.
 */
export const getSectionChipFromNode = (node) => {
  if (!node?.title) {
    return null;
  }

  const sectionNumbers = getChapterNumbersFromTitle(node.title);
  const label = sectionNumbers.length
    ? sectionNumbers.join('.')
    : node.title.trim();

  if (!label) {
    return null;
  }

  return {
    id: node.id,
    label
  };
};

/** @deprecated Use getSectionChipFromNode */
export const getSectionNumbersFromNode = (node) => {
  const chip = getSectionChipFromNode(node);

  return chip ? [chip.label] : [];
};

/** Flatten unique section chips from all nodes on an assignment. */
export const getDisplaySectionsFromContentNodes = (contentNodes = []) => {
  const seenIds = new Set();

  return (contentNodes || []).reduce((sections, node) => {
    const chip = getSectionChipFromNode(node);

    if (!chip || seenIds.has(chip.id)) {
      return sections;
    }

    seenIds.add(chip.id);
    sections.push(chip);

    return sections;
  }, []);
};

/** Merge a dragged tree node into a single assignment's content payload. */
export const applyDraggedNodeToAssignment = (assignment, nodePayload, sourceTocData = []) => {
  const sourceNode = findNodeById(sourceTocData, nodePayload.id) || nodePayload;
  const node = JSON.parse(JSON.stringify(sourceNode));
  const existingNodes = assignment.contentNodes || [];
  const alreadyAdded = existingNodes.some((item) => item.id === node.id);
  const nextContentNodes = alreadyAdded ? existingNodes : [...existingNodes, node];
  const nextSections = getDisplaySectionsFromContentNodes(nextContentNodes);
  const nextChapters = nextContentNodes.filter((item) => item.type === 'chapter' || item.type === 'module');

  return {
    ...assignment,
    sections: nextSections,
    chapters: nextChapters,
    contentNodes: nextContentNodes
  };
};

/**
 * Attach a tree node to the assignment for the chosen date.
 * Creates a new card when no slot exists for that date.
 */
export function applyAddNodeToDate({
  assignments = [],
  nodePayload,
  targetDateIso,
  sourceTocData = [],
  scheduleFormValues = {}
}) {
  if (!nodePayload?.id || !targetDateIso) {
    return { assignments, assignedNodeIds: collectAssignedContentNodeIds(assignments) };
  }

  const unassignedNode = getUnassignedNodeForSchedule(
    nodePayload,
    sourceTocData,
    assignments
  );

  if (!unassignedNode) {
    return { assignments, assignedNodeIds: collectAssignedContentNodeIds(assignments) };
  }

  const existingAssignment = findAssignmentByDate(assignments, targetDateIso);

  if (existingAssignment && assignmentHasContent(existingAssignment)) {
    return { assignments, assignedNodeIds: collectAssignedContentNodeIds(assignments) };
  }

  const baseAssignment = existingAssignment || createAssignmentForDate({
    dateIso: targetDateIso,
    scheduleFormValues,
    assignments
  });
  const updatedAssignment = applyDraggedNodeToAssignment(
    baseAssignment,
    unassignedNode,
    sourceTocData
  );
  const nextAssignments = upsertAssignmentByDate(assignments, updatedAssignment);

  return {
    assignments: nextAssignments,
    assignedNodeIds: collectAssignedContentNodeIds(nextAssignments),
    targetAssignmentId: updatedAssignment.id
  };
}

/**
 * Copy populated content from one assignment card onto another date.
 * Original card is left unchanged; content is merged into the target date card.
 */
export function applyAddAssignmentContentToDate({
  assignments = [],
  sourceAssignmentId,
  targetDateIso,
  sourceTocData = [],
  scheduleFormValues = {}
}) {
  const sourceAssignment = assignments.find((item) => item.id === sourceAssignmentId);

  if (!sourceAssignment || !targetDateIso) {
    return { assignments, assignedNodeIds: collectAssignedContentNodeIds(assignments) };
  }

  if (sourceAssignment.date === targetDateIso) {
    return { assignments, assignedNodeIds: collectAssignedContentNodeIds(assignments) };
  }

  let targetAssignment = findAssignmentByDate(assignments, targetDateIso);

  if (!targetAssignment) {
    targetAssignment = createAssignmentForDate({
      dateIso: targetDateIso,
      scheduleFormValues,
      assignments,
      title: sourceAssignment.title
    });
  }

  const contentNodes = sourceAssignment.contentNodes || [];
  let mergedAssignment = { ...targetAssignment };

  contentNodes.forEach((node) => {
    mergedAssignment = applyDraggedNodeToAssignment(mergedAssignment, node, sourceTocData);
  });

  const nextAssignments = upsertAssignmentByDate(assignments, mergedAssignment);

  return {
    assignments: nextAssignments,
    assignedNodeIds: collectAssignedContentNodeIds(nextAssignments),
    targetAssignmentId: mergedAssignment.id
  };
}

/** Persist an inline title edit from the schedule builder card. */
export function applyAssignmentTitleUpdate({
  assignments = [],
  assignmentId,
  title
} = {}) {
  const trimmedTitle = String(title || '').trim();

  const nextAssignments = assignments.map((assignment) => {
    if (assignment.id !== assignmentId) {
      return assignment;
    }

    return {
      ...assignment,
      title: trimmedTitle || assignment.title
    };
  });

  return {
    assignments: nextAssignments,
    assignedNodeIds: collectAssignedContentNodeIds(nextAssignments)
  };
}

/** Total question count across an assignment's assigned content nodes. */
export function getQuickScheduleSlotQuestionCount(
  assignment,
  { questionCounts = [], sourceTocData = [] } = {}
) {
  const contentNodes = assignment?.contentNodes || [];

  return contentNodes.reduce(
    (sum, node) => sum + calculateQuestionsForNode(node, questionCounts, sourceTocData),
    0
  );
}

/**
 * Checks whether a populated slot needs the inline "can't be published" warning
 */
export function hasQuickScheduleSlotZeroQuestions(assignment, options = {}) {
  if (!assignmentHasContent(assignment)) {
    return false;
  }

  return getQuickScheduleSlotQuestionCount(assignment, options) === 0;
}

/**
 * Whether at least one schedule slot has content with 1+ questions.
 * Save/Finish enables as soon as this is true
 */
export function hasAnyQuickScheduleSlotWithQuestions(assignments = [], options = {}) {
  if (!Array.isArray(assignments) || !assignments.length) {
    return false;
  }

  return assignments.some((assignment) => (
    assignmentHasContent(assignment) && getQuickScheduleSlotQuestionCount(assignment, options) > 0
  ));
}

/**
 * Generate empty assignment slots for each configured due weekday between start and end.
 */
export const buildQuickScheduleAssignments = (formValues = {}) => {
  const {
    dueDays = [],
    dueTime = '',
    startDate = '',
    endDate = ''
  } = formValues;

  if (!dueDays.length || !startDate || !endDate) {
    return [];
  }

  const start = parseScheduleCalendarDate(startDate);
  const end = parseScheduleCalendarDate(endDate);

  if (!start || !end) {
    return [];
  }

  const dueDayIndexes = new Set(
    dueDays.map((dayKey) => DAY_KEY_TO_INDEX[dayKey]).filter((value) => value !== undefined)
  );

  const assignments = [];
  let assignmentIndex = 1;
  const startMs = toLocalCalendarDate(start).getTime();
  const endMs = toLocalCalendarDate(end).getTime();

  for (let cursorMs = startMs; cursorMs <= endMs; cursorMs += MS_PER_DAY) {
    const cursorDate = new Date(cursorMs);

    if (dueDayIndexes.has(cursorDate.getDay())) {
      assignments.push({
        id: `${PREFIX_ASSIGNMENT}-qs-${assignmentIndex}`,
        title: `Assignment ${assignmentIndex}`,
        date: formatDateIso(cursorDate),
        time: dueTime,
        sections: [],
        chapters: [],
        contentNodes: []
      });
      assignmentIndex += 1;
    }
  }

  return assignments;
};

export const removeSectionFromAssignment = (assignment, sectionKey) => {
  const normalizedKey = String(sectionKey);

  const nextContentNodes = (assignment.contentNodes || []).filter((node) => {
    if (node.id === sectionKey) {
      return false;
    }

    const chip = getSectionChipFromNode(node);

    return chip?.label !== normalizedKey && chip?.id !== sectionKey;
  });
  const nextSections = getDisplaySectionsFromContentNodes(nextContentNodes);
  const nextChapters = nextContentNodes.filter(
    (item) => item.type === 'chapter' || item.type === 'module'
  );

  return {
    ...assignment,
    sections: nextSections,
    chapters: nextChapters,
    contentNodes: nextContentNodes
  };
};

export const clearAssignmentContent = (assignment) => ({
  ...assignment,
  sections: [],
  chapters: [],
  contentNodes: []
});

/** Drop handler — attach dragged content to the targeted assignment slot. */
export const applyContentDrop = ({
  assignments = [],
  assignmentId,
  nodePayload,
  sourceTocData = []
}) => {
  const nextAssignments = assignments.map((assignment) => {
    if (assignment.id !== assignmentId) {
      return assignment;
    }

    return applyDraggedNodeToAssignment(assignment, nodePayload, sourceTocData);
  });

  return {
    assignments: nextAssignments,
    assignedNodeIds: collectAssignedContentNodeIds(nextAssignments)
  };
};

/** Remove one section chip and its backing content node from an assignment. */
export const applySectionRemove = ({
  assignments = [],
  assignmentId,
  sectionNumber
}) => {
  const nextAssignments = assignments.map((assignment) => {
    if (assignment.id !== assignmentId) {
      return assignment;
    }

    return removeSectionFromAssignment(assignment, sectionNumber);
  });

  return {
    assignments: nextAssignments,
    assignedNodeIds: collectAssignedContentNodeIds(nextAssignments)
  };
};

/** Clear all content from an assignment while keeping the scheduled date slot. */
export const applyClearAssignmentContent = ({
  assignments = [],
  assignmentId
}) => {
  const nextAssignments = assignments.map((assignment) => (
    assignment.id === assignmentId ? clearAssignmentContent(assignment) : assignment
  ));

  return {
    assignments: nextAssignments,
    assignedNodeIds: collectAssignedContentNodeIds(nextAssignments)
  };
};

/**
 * Move a schedule card to another date within the step 1 range.
 * Occupied dates are disabled in the picker; empty slots swap dates.
 */
export function applyRescheduleAssignmentDate({
  assignments = [],
  assignmentId,
  targetDateIso
}) {
  if (!assignmentId || !targetDateIso) {
    return { assignments, assignedNodeIds: collectAssignedContentNodeIds(assignments) };
  }

  const sourceIndex = assignments.findIndex((item) => item.id === assignmentId);

  if (sourceIndex < 0) {
    return { assignments, assignedNodeIds: collectAssignedContentNodeIds(assignments) };
  }

  const sourceAssignment = assignments[sourceIndex];

  if (sourceAssignment.date === targetDateIso) {
    return {
      assignments,
      assignedNodeIds: collectAssignedContentNodeIds(assignments),
      targetAssignmentId: sourceAssignment.id
    };
  }

  const targetIndex = assignments.findIndex((item) => item.date === targetDateIso);

  if (targetIndex >= 0 && assignmentHasContent(assignments[targetIndex])) {
    return {
      assignments,
      assignedNodeIds: collectAssignedContentNodeIds(assignments),
      targetAssignmentId: sourceAssignment.id
    };
  }

  const nextAssignments = [...assignments];

  if (targetIndex < 0) {
    nextAssignments[sourceIndex] = {
      ...sourceAssignment,
      date: targetDateIso
    };
  } else {
    const targetAssignment = assignments[targetIndex];

    nextAssignments[sourceIndex] = { ...sourceAssignment, date: targetAssignment.date };
    nextAssignments[targetIndex] = { ...targetAssignment, date: sourceAssignment.date };
  }

  const sortedAssignments = nextAssignments.sort((left, right) => (
    String(left.date).localeCompare(String(right.date))
  ));

  return {
    assignments: sortedAssignments,
    assignedNodeIds: collectAssignedContentNodeIds(sortedAssignments),
    targetAssignmentId: sourceAssignment.id
  };
}

/** Remove an empty schedule slot card from the builder list. */
export function applyRemoveAssignmentSlot({
  assignments = [],
  assignmentId
} = {}) {
  const nextAssignments = assignments.filter((assignment) => assignment.id !== assignmentId);

  return {
    assignments: nextAssignments,
    assignedNodeIds: collectAssignedContentNodeIds(nextAssignments)
  };
}
